import React, { useMemo, useState, useEffect } from "react";
import type { ReactNode } from "react";
import {
  Col,
  Form,
  Input,
  Row,
  Select,
  Tooltip,
  DatePicker,
  Checkbox,
  Divider,
  Spin,
  Modal,
} from "antd";
import type { FormInstance } from "antd/es/form/Form";
import type { Moment } from "moment";
import NumberFormat from "react-number-format";
import { useAppSelector } from "../../..";
import { checkIsLoading, displayFormat, getFio, getSelectOptions } from "../../../lib/utils";
import { searchIcon, crossIcon } from "../../../lib/icons";
import { ReactComponent as CancelButtonIcon } from "../../../img/buttons_icons/cancelButton_icon.svg";
import { ReactComponent as SubmitButtonIcon } from "../../../img/buttons_icons/submitButton_icon.svg";
import {
  IChangingMode,
  IClientType,
  ICreateMode,
  IOfferFieldName,
  Offer,
} from "../../../lib/types";
import type {
  IOfferListSelectMode,
  IOfferParamsFormData,
} from "../../../containers/OfferContainer/context/types";
import { isFieldAvailable } from "./utils/isFieldAvailable";
import { isFieldRequired } from "./utils/isFieldRequired";
import { OFFER_STATUSES } from "../../../store/slices/offering/const";
import { DictionaryDto, Property } from "../../../lib/interfaces";
import { IOfferListSearch } from "../../../store/slices/offering/types";
import { AgreementsSearchModal } from "../../AgreementsSearch";
import { TariffModelTypes } from "../../../lib/const/tariffModelTypes";
import { lifespanPeriodIdsWithoutQty } from "../../../lib/const/lifespanPeriodIdsWithoutQty";
import { hasAccess } from "../../RolesModule/hasAccess";
import {
  MENU_ACCOUNTS,
  MENU_ACCOUNTS_INDIVIDUAL,
  MENU_ACCOUNTS_INDIVIDUAL_WRITE,
  MENU_ACCOUNTS_ORGANIZATION,
  MENU_ACCOUNTS_ORGANIZATION_WRITE,
  MENU_ACCOUNTS_WRITE,
} from "../../../lib/const/permissions";
import "./OfferParamsEdit.less";

const formItemLayout = {
  labelCol: { span: 10 },
  wrapperCol: { span: 14 },
  className: "form-item-required__hidden-error__mb0",
};

const rowLayout = {
  gutter: 50,
  style: {
    marginTop: "10px",
  },
};

interface ComponentProps {
  offer?: Offer;
  viewMode: IChangingMode | ICreateMode;
  onCancel?: () => void;
  onSubmit?: (mode: IChangingMode) => void;
  isLoading?: boolean;
  onChangeOfferingTypeId?: (id: number) => void;
  onChangeTariffModelId: (id: TariffModelTypes) => void;
  onNameBlur: (props: {
    viewMode?: IChangingMode | ICreateMode;
    name?: string;
    offerVersions?: Offer[];
  }) => Promise<boolean>;
  allowedOfferingStatuses: DictionaryDto[];
  offerParamsForm: FormInstance<IOfferParamsFormData>;
  searchListValues: IOfferListSearch;
  offerListSelectMode?: IOfferListSelectMode;
  updateTaxRate: (date: Moment | null) => void;
  disabledFields?: IOfferFieldName[]; // Для частных случаев, общие правила определены в isFieldAvailable
  offerVersions?: Offer[]; // нужны для передачи в onNameBlur для проверки уникальности имени: актуально только для viewMode add & edit
  onChangeFromDateCharacteristic?: (fromDate?: moment.Moment | null) => void;
  isCommonOfferingType?: boolean;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const OfferParamsEdit = (props: ComponentProps) => {
  const {
    offer,
    viewMode,
    onCancel,
    onSubmit,
    isLoading = false,
    onChangeOfferingTypeId,
    onNameBlur,
    allowedOfferingStatuses,
    offerParamsForm,
    updateTaxRate,
    disabledFields = [],
    offerVersions,
    onChangeTariffModelId,
    onChangeFromDateCharacteristic,
    isCommonOfferingType,
  } = props;
  const {
    offeringAccessTypes,
    offeringAccessTypesLoading,
    offeringStatusesLoading,
    offeringProlongTypes,
    offeringProlongTypesLoading,
    lifespanPeriods,
    lifespanPeriodsLoading,
    offeringBundleTypes,
    offeringBundleTypesLoading,
  } = useAppSelector((state) => state.dicts);

  const {
    tariffPeriodAlignments,
    tariffPeriodAlignmentsLoading,
    tariffModelTypes,
    tariffModelTypesLoading,
  } = useAppSelector((state) => state.rtkDictionaries);
  const { offerHierarchy } = useAppSelector((state) => state.offering);

  const tenantPropertiesDto = useAppSelector(
    (state) => state.tenants.tenantPropertiesDto
  ) as Property[];

  const productCatalogMode = useMemo(() => {
    const propertyObj = tenantPropertiesDto.find(
      ({ propertyType }) => propertyType?.alias === "product.catalog.mode"
    );
    if (propertyObj) {
      return propertyObj.numberValue;
    }
    return -1;
  }, [tenantPropertiesDto]);

  const [searchModalVisible, setSearchModalVisible] = useState(false);

  const [selectedAgreement, setSelectedAgreement] = useState<{
    clientName?: string;
    agreementNumber?: string;
    id?: number;
  }>();

  // Тип клиента в поиске договора
  const [clientType, setClientType] = useState<IClientType>("ORGANIZATION");

  const hasAccessToOrganization = useMemo(
    () =>
      hasAccess(
        MENU_ACCOUNTS_ORGANIZATION,
        MENU_ACCOUNTS_ORGANIZATION_WRITE,
        MENU_ACCOUNTS,
        MENU_ACCOUNTS_WRITE
      ),
    [hasAccess]
  );

  const hasAccessToIndividual = useMemo(
    () => hasAccess(MENU_ACCOUNTS_INDIVIDUAL, MENU_ACCOUNTS_INDIVIDUAL_WRITE),
    [hasAccess]
  );

  // Кнопка изменения задизейблена, если доступен только один вид клиента
  const allowedClientChange = useMemo(() => {
    if (hasAccessToOrganization && hasAccessToIndividual) {
      return true;
    }
    return false;
  }, [hasAccessToOrganization, hasAccessToIndividual]);

  // Дефолтный тип клиента в поиске ЮЛ/ФЛ
  useEffect(() => {
    if (hasAccessToOrganization) {
      setClientType("ORGANIZATION");
    } else if (hasAccessToIndividual) {
      setClientType("INDIVIDUAL");
    }
  }, [hasAccessToOrganization, hasAccessToIndividual]);

  const toggleClientType = () => {
    if (allowedClientChange) {
      setClientType((prev) => (prev === "INDIVIDUAL" ? "ORGANIZATION" : "INDIVIDUAL"));
      resetSelectedAgreement();
    }
  };

  const offeringStatusId = useMemo(() => {
    if (viewMode === "create") return OFFER_STATUSES.PREPARE;
    return offer?.offeringStatusId;
  }, [viewMode, offer]);

  const creatingOfferingTypeId = useMemo(() => {
    if (viewMode === "create") {
      return isCommonOfferingType === true ? 1 : 3;
    }
  }, [viewMode, isCommonOfferingType]);

  const getRequired = (fieldName: IOfferFieldName): boolean => {
    const formValues = offerParamsForm.getFieldsValue();
    if (offeringStatusId) {
      return isFieldRequired({
        fieldName,
        viewMode,
        offeringStatusId,
        options: {
          offeringTypeId:
            viewMode === "create" ? creatingOfferingTypeId : formValues.offeringTypeId,
          offeringStatusId: formValues.offeringStatusId,
        },
      });
    }
    return false;
  };

  const getAvailable = (fieldName: IOfferFieldName): boolean => {
    const formValues = offerParamsForm.getFieldsValue();
    if (disabledFields.includes(fieldName)) return false;
    if (offeringStatusId) {
      return isFieldAvailable({
        fieldName,
        viewMode,
        offeringStatusId,
        options: {
          offeringTypeId: formValues.offeringTypeId,
          tariffModelId: formValues.tariffModelId,
        },
      });
    }
    return false;
  };

  // console.log(selectedOffer);

  // Фиксация данных по договору для отображения, при создании на основании или редактировании
  useEffect(() => {
    if (offer?.agreement) {
      const { account, agreementNumber, id } = offer.agreement;
      const fio = account.individual && getFio(account.individual);
      const fullName = account?.organization?.fullName;
      setSelectedAgreement({
        id,
        agreementNumber,
        clientName: fullName || fio,
      });
    }
  }, [offer]);

  const closeSearchModal = () => {
    setSearchModalVisible(false);
  };

  const resetSelectedAgreement = () => {
    offerParamsForm.resetFields(["agreementId"]);
    setSelectedAgreement(undefined);
  };

  const onClickAgreementInput = () => {
    if (selectedAgreement) {
      resetSelectedAgreement();
    } else {
      setSearchModalVisible(true);
    }
  };

  const onAgreementSelect = (
    accountIds: number[],
    agreement?: { agreementId: number; agreementNumber: string; fullName: string }[]
  ) => {
    if (agreement) {
      const { agreementId, agreementNumber, fullName } = agreement[0];
      setSelectedAgreement({
        id: agreementId,
        agreementNumber,
        clientName: fullName,
      });
      offerParamsForm.setFieldsValue({ agreementId });
      closeSearchModal();
    }
  };

  const selectedAgreementTitle = useMemo((): ReactNode | "" => {
    if (selectedAgreement) {
      const agreementData = Object.values(selectedAgreement).filter(Boolean);
      return agreementData.map((item, idx) => <span key={idx}>{item}</span>);
    }
    return "";
  }, [selectedAgreement]);

  // Id типов "индивидуальный" предложения и профиля не совпадают, для этого синхронизация. Только в режиме создания
  const handleChangeOfferingTypeId = (id: number) => {
    onChangeOfferingTypeId && onChangeOfferingTypeId(id);
    // Очистка данных по договору для отображения (в режиме создания), при изменении типа предложения
    if (viewMode === "create") {
      setSelectedAgreement(undefined);
    }
  };

  const disableFromDate = (date: Moment): boolean => {
    if (viewMode === "create") {
      const toDate = offerParamsForm.getFieldValue("toDate");
      return toDate ? date.isAfter(toDate, "day") : false;
    }
    return !date.isBetween(offer?.fromDate, offer?.toDate, "day", "(]");
  };

  const disableToDate = (date: Moment): boolean => {
    if (viewMode === "create") {
      const fromDate = offerParamsForm.getFieldValue("fromDate");
      return fromDate ? date.isBefore(fromDate, "day") : false;
    }
    return !date.isBetween(offer?.fromDate, offer?.toDate, "day", "[]");
  };

  const handleOnCancel = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    onCancel && onCancel();
  };

  const handleOnSubmit = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    viewMode !== "create" && onSubmit && onSubmit(viewMode);
  };

  const renderButtons = () =>
    viewMode !== "create" ? (
      <Row align="middle" className="offer-params-edit__btns">
        <Col>
          <Tooltip title="Подтвердить">
            <SubmitButtonIcon onClick={handleOnSubmit} />
          </Tooltip>
        </Col>
        <Col>
          <Tooltip title="Отменить">
            <CancelButtonIcon onClick={handleOnCancel} />
          </Tooltip>
        </Col>
      </Row>
    ) : null;

  return (
    <div className="offer-params-edit">
      <Spin spinning={isLoading}>
        <Divider style={{ margin: "0 0 9px" }} />
        <Row {...rowLayout}>
          <Col span={6}>
            <Form.Item
              label="Наименование"
              name="extName"
              rules={[{ required: getRequired("extName"), message: "" }]}
              {...formItemLayout}
            >
              <Input allowClear disabled={!getAvailable("extName")} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Тип пролонгации"
              name="prolongTypeId"
              {...formItemLayout}
              rules={[{ required: getRequired("prolongTypeId"), message: "" }]}
            >
              <Select
                loading={checkIsLoading(offeringProlongTypesLoading)}
                options={getSelectOptions({
                  list: offeringProlongTypes,
                })}
                disabled={!getAvailable("prolongTypeId")}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Тип"
              name="offeringTypeId"
              {...formItemLayout}
              rules={[{ required: getRequired("offeringTypeId"), message: "" }]}
            >
              <Select
                loading={checkIsLoading(offeringAccessTypesLoading)}
                options={getSelectOptions({
                  list: offeringAccessTypes,
                })}
                onSelect={handleChangeOfferingTypeId}
                disabled={!getAvailable("offeringTypeId")}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Статус"
              name="offeringStatusId"
              {...formItemLayout}
              rules={[{ required: getRequired("offeringStatusId"), message: "" }]}
              labelCol={{ span: 9 }}
              wrapperCol={{ span: 9 }}
            >
              <Select
                disabled={!getAvailable("offeringStatusId")}
                loading={checkIsLoading(offeringStatusesLoading)}
                options={getSelectOptions({
                  list: allowedOfferingStatuses,
                })}
              />
            </Form.Item>
          </Col>
          {renderButtons()}
        </Row>
        <Row {...rowLayout}>
          <Col span={6}>
            <Form.Item
              label="Код"
              name="name"
              rules={[{ required: getRequired("name"), message: "" }]}
              {...formItemLayout}
            >
              <Input
                allowClear
                disabled={!getAvailable("name")}
                onBlur={(e) =>
                  onNameBlur({
                    viewMode,
                    name: e.target.value,
                    offerVersions,
                  }).then((result) => {
                    if (result === false) {
                      offerParamsForm.setFields([
                        {
                          name: "name",
                          errors: [""],
                        },
                      ]);
                    }
                  })
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Row gutter={5}>
              <Col span={20}>
                <Form.Item
                  label="Период продукта"
                  name="lifespanPeriodId"
                  rules={[{ required: getRequired("lifespanPeriodId"), message: "" }]}
                  {...formItemLayout}
                  labelCol={{ span: 12 }}
                  wrapperCol={{ span: 12 }}
                >
                  <Select
                    allowClear
                    options={getSelectOptions({
                      list: lifespanPeriods,
                    })}
                    loading={checkIsLoading(lifespanPeriodsLoading)}
                    disabled={!getAvailable("lifespanPeriodId")}
                    onSelect={(id: number) => {
                      if (lifespanPeriodIdsWithoutQty.includes(id)) {
                        offerParamsForm.setFieldsValue({ lifespanPeriodQty: 1 });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Tooltip title="Количество: целое от 1 до 30000">
                  <Form.Item noStyle dependencies={["lifespanPeriodId"]}>
                    {({ getFieldValue }) => {
                      const lifespanPeriodId = getFieldValue("lifespanPeriodId");
                      return (
                        <Form.Item
                          name="lifespanPeriodQty"
                          rules={[
                            { required: getRequired("lifespanPeriodQty"), message: "" },
                            {
                              validator: (_, qty) => {
                                const isQtyValid = qty >= 1 && qty <= 30000;
                                const isInteger = Number.isInteger(Number(qty));
                                const withoutE = !String(qty).includes("e");
                                if (isQtyValid && isInteger && withoutE) {
                                  return Promise.resolve();
                                }
                                return Promise.reject();
                              },
                              message: "",
                            },
                          ]}
                          {...formItemLayout}
                          wrapperCol={{ span: 24 }}
                        >
                          <NumberFormat
                            className="ant-input-affix-wrapper inputNumber__hideArrows"
                            decimalScale={0}
                            fixedDecimalScale
                            allowNegative={false}
                            disabled={
                              !getAvailable("lifespanPeriodQty") ||
                              lifespanPeriodIdsWithoutQty.includes(lifespanPeriodId)
                            }
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Tooltip>
              </Col>
            </Row>
          </Col>
          <Col span={6}>
            {productCatalogMode === 1 && (
              <Tooltip
                title={selectedAgreementTitle}
                overlayClassName="offer-params-edit__agreementData"
                placement="bottom"
              >
                <Form.Item
                  label="Договор"
                  name="agreementId"
                  {...formItemLayout}
                  rules={[{ required: getRequired("agreementId"), message: "" }]}
                >
                  <div className="offer-params-edit__agreementSearch">
                    <Input
                      style={{
                        cursor: getAvailable("agreementId") ? "pointer" : "default",
                        textOverflow: "ellipsis",
                        paddingRight: "25px",
                      }}
                      value={selectedAgreement?.agreementNumber}
                      onClick={onClickAgreementInput}
                      disabled={!getAvailable("agreementId")}
                    />

                    {getAvailable("agreementId") && (
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={onClickAgreementInput}
                        className="offer-params-edit__searchIcon"
                      >
                        <Tooltip title={selectedAgreement ? "Удалить договор" : "Найти договор"}>
                          {selectedAgreement ? crossIcon("#334455") : searchIcon("#334455")}
                        </Tooltip>
                      </span>
                    )}
                  </div>
                </Form.Item>
              </Tooltip>
            )}
            {productCatalogMode === 0 && (
              <Form.Item
                label="ID договора"
                name="agreementId"
                {...formItemLayout}
                rules={[{ required: getRequired("agreementId"), message: "" }]}
              >
                <Input disabled={!getAvailable("agreementId")} type="number" />
              </Form.Item>
            )}
          </Col>
          <Col span={6}>
            <Form.Item
              label="Доступ к подкл."
              name="isStandalone"
              {...formItemLayout}
              labelCol={{ span: 9 }}
              wrapperCol={{ span: 9 }}
              valuePropName="checked"
            >
              <Checkbox disabled={!getAvailable("isStandalone")} />
            </Form.Item>
          </Col>
        </Row>
        <Row {...rowLayout}>
          <Col span={6}>
            <Form.Item shouldUpdate className="form-item-required__hidden-error__mb0">
              {({ getFieldValue, setFieldsValue }) => {
                const bundleTypeId = getFieldValue("bundleTypeId");
                const onSelectBundleTypeId = (id: number) => {
                  if (
                    bundleTypeId !== 1 &&
                    id === 1 &&
                    offerHierarchy?.children?.length &&
                    (viewMode === "edit" || viewMode === "add")
                  ) {
                    setFieldsValue({ bundleTypeId });
                    Modal.error({
                      content: "Необходимо закрыть или удалить связанные Предложения",
                      width: "500px",
                    });
                  }
                };
                return (
                  <Form.Item
                    label="Тип пакета"
                    name="bundleTypeId"
                    {...formItemLayout}
                    rules={[{ required: getRequired("bundleTypeId"), message: "" }]}
                  >
                    <Select
                      disabled={!getAvailable("bundleTypeId")}
                      options={getSelectOptions({
                        list: offeringBundleTypes,
                      })}
                      loading={checkIsLoading(offeringBundleTypesLoading)}
                      showArrow={getAvailable("bundleTypeId")}
                      onSelect={onSelectBundleTypeId}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              dependencies={["tariffModelId"]}
              className="form-item-required__hidden-error__mb0"
            >
              {() => (
                <Form.Item
                  label="Смещение периода"
                  name="periodAlignmentId"
                  {...formItemLayout}
                  rules={[{ required: getRequired("periodAlignmentId"), message: "" }]}
                >
                  <Select
                    disabled={!getAvailable("periodAlignmentId")}
                    options={getSelectOptions({
                      list: tariffPeriodAlignments,
                    })}
                    loading={checkIsLoading(tariffPeriodAlignmentsLoading)}
                    showArrow={getAvailable("periodAlignmentId")}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Col>
          <Col span={6} offset={6}>
            <Form.Item
              label="Дата создания"
              name="fromDate"
              rules={[{ required: getRequired("fromDate"), message: "" }]}
              {...formItemLayout}
              labelCol={{ span: 9 }}
              wrapperCol={{ span: 9 }}
            >
              <DatePicker
                format={displayFormat}
                onChange={(e) => {
                  updateTaxRate(e);
                  onChangeFromDateCharacteristic && onChangeFromDateCharacteristic(e);
                }}
                disabled={!getAvailable("fromDate")}
                disabledDate={disableFromDate}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row {...rowLayout}>
          <Col span={6} offset={6}>
            <Form.Item
              label="Бизнес-модель"
              name="tariffModelId"
              {...formItemLayout}
              rules={[{ required: getRequired("tariffModelId"), message: "" }]}
            >
              <Select
                disabled={!getAvailable("tariffModelId")}
                options={getSelectOptions({
                  list: tariffModelTypes,
                })}
                loading={checkIsLoading(tariffModelTypesLoading)}
                showArrow={getAvailable("tariffModelId")}
                onChange={onChangeTariffModelId}
              />
            </Form.Item>
          </Col>
          <Col span={6} offset={6}>
            <Form.Item
              label="Дата окончания"
              name="toDate"
              rules={[{ required: getRequired("toDate"), message: "" }]}
              {...formItemLayout}
              labelCol={{ span: 9 }}
              wrapperCol={{ span: 9 }}
            >
              <DatePicker
                format={displayFormat}
                disabled={!getAvailable("toDate")}
                disabledDate={disableToDate}
              />
            </Form.Item>
          </Col>
        </Row>
      </Spin>
      <AgreementsSearchModal
        clientType={clientType}
        visible={searchModalVisible}
        onClose={closeSearchModal}
        onSubmit={onAgreementSelect}
        toggleClientType={toggleClientType}
        okText="Выбрать договор"
      />
    </div>
  );
};
